import { Avatar, Card, CardBody, CardHeader, Chip, Divider } from '@heroui/react';
import { Building2, Calendar, CheckCircle, Clock, Key, Mail, Phone, Shield, User as UserIcon, UserCircle, XCircle } from 'lucide-react';
import React, { FC } from 'react';
import { useAuthState } from 'src/state/authSate';

import { ContentLoadError, PageTitle, Spinner } from '../../../components';
import { useGetUser } from '../../../queries/usersQuery';
import { formatFirestoreDate } from '../../../utils/formatFirestoreDate';

export const Profile: FC = () => {
  const { currentUser } = useAuthState();

  const { data: userData, isLoading, error, refetch } = useGetUser(currentUser?.uid);

  if (!currentUser) {
    return (
      <ContentLoadError
        message="Please log in to view your profile"
        onRefreshClicked={() => window.location.href = '/login'}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Spinner showLabel={false} />
        <div>
          <p className="text-lg font-semibold">Loading Profile</p>
          <p className="text-sm text-gray-500">Fetching your profile information</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <ContentLoadError
        message={error.message || 'An error occurred while loading your profile'}
        onRefreshClicked={() => refetch()}
      />
    );
  }

  const profileData = userData || currentUser;
  const { displayName, email, photoURL, name, surname, phoneNumber, role, createdAt, organizations, permissions } = profileData;
  const fullName = displayName || `${name || ''} ${surname || ''}`.trim() || 'User';

  return (
    <div className="max-w-7xl mx-auto">
      <PageTitle
        title="My Profile"
        buttonText="Edit Profile"
        buttonOnClick={() => { /* TODO: Navigate to edit profile */ }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Left Column - Profile Overview */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Card */}
          <Card className="shadow-sm">
            <CardBody className="p-6 text-center">
              <div className="flex flex-col items-center">
                <div className="relative">
                  <Avatar
                    src={photoURL || ''}
                    className="w-32 h-32"
                    name={fullName}
                    fallback={
                      <UserCircle className="w-24 h-24 text-gray-400" />
                    }
                  />
                  {currentUser.emailVerified && (
                    <div className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-lg">
                      <CheckCircle className="w-6 h-6 text-green-500" />
                    </div>
                  )}
                </div>
                <h2 className="text-2xl font-bold mt-4 text-gray-900">{fullName}</h2>
                <p className="text-gray-600 text-sm mt-1">{email}</p>
                <div className="mt-4">
                  <Chip
                    color={role === 'admin' ? 'danger' : role === 'manager' ? 'warning' : 'primary'}
                    variant="flat"
                    className="font-semibold"
                  >
                    {typeof role === 'string' ? role.toUpperCase() : 'USER'}
                  </Chip>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Quick Stats */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3 px-6">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Quick Info</h3>
            </CardHeader>
            <Divider />
            <CardBody className="px-6 py-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Member Since</span>
                <span className="text-sm font-medium">
                  {createdAt ? formatFirestoreDate(createdAt, { year: 'numeric', month: 'short' }) : 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <Chip size="sm" color="success" variant="flat">Active</Chip>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Organizations</span>
                <span className="text-sm font-medium">{organizations?.length || 0}</span>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Right Column - Detailed Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3 px-6 bg-gray-50">
              <div className="flex items-center gap-2">
                <UserIcon size={18} className="text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Personal Information</h3>
              </div>
            </CardHeader>
            <CardBody className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">First Name</label>
                  <p className="text-base font-medium text-gray-900">{name || 'Not provided'}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Name</label>
                  <p className="text-base font-medium text-gray-900">{surname || 'Not provided'}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Display Name</label>
                  <p className="text-base font-medium text-gray-900">{displayName || 'Not set'}</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">User ID</label>
                  <p className="text-sm font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded">{currentUser.uid}</p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Contact Information */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3 px-6 bg-gray-50">
              <div className="flex items-center gap-2">
                <Mail size={18} className="text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
              </div>
            </CardHeader>
            <CardBody className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email Address</label>
                  <div className="flex items-center gap-2">
                    <p className="text-base font-medium text-gray-900">{email || 'Not provided'}</p>
                    {currentUser.emailVerified && (
                      <CheckCircle size={16} className="text-green-500" />
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone Number</label>
                  <p className="text-base font-medium text-gray-900">{phoneNumber || 'Not provided'}</p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Access & Permissions */}
          {(organizations?.length > 0 || permissions?.length > 0) && (
            <Card className="shadow-sm">
              <CardHeader className="pb-3 px-6 bg-gray-50">
                <div className="flex items-center gap-2">
                  <Shield size={18} className="text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Access & Permissions</h3>
                </div>
              </CardHeader>
              <CardBody className="p-6 space-y-4">
                {organizations?.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 block">Organizations</label>
                    <div className="flex flex-wrap gap-2">
                      {organizations.map((org: string) => (
                        <Chip key={org} variant="flat" className="bg-blue-50 text-blue-700">
                          <div className="flex items-center gap-1">
                            <Building2 size={14} />
                            {org}
                          </div>
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}

                {permissions?.length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3 block">Permissions</label>
                    <div className="flex flex-wrap gap-2">
                      {permissions.map((perm: any) => (
                        <Chip key={perm} variant="flat" className="bg-purple-50 text-purple-700">
                          <div className="flex items-center gap-1">
                            <Key size={14} />
                            {perm}
                          </div>
                        </Chip>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Account Details */}
          <Card className="shadow-sm">
            <CardHeader className="pb-3 px-6 bg-gray-50">
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Account Details</h3>
              </div>
            </CardHeader>
            <CardBody className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Account Created</label>
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-gray-500" />
                    <p className="text-base font-medium text-gray-900">
                      {createdAt ? formatFirestoreDate(createdAt, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }) : 'Unknown'}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Last Sign In</label>
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-gray-500" />
                    <p className="text-base font-medium text-gray-900">
                      {currentUser.metadata?.lastSignInTime
                        ? new Date(currentUser.metadata.lastSignInTime).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                        : 'Unknown'}
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email Status</label>
                  <Chip
                    color={currentUser.emailVerified ? 'success' : 'warning'}
                    variant="flat"
                    size="sm"
                    className="font-medium"
                    startContent={currentUser.emailVerified ? <CheckCircle size={14} /> : <XCircle size={14} />}
                  >
                    {currentUser.emailVerified ? 'Verified' : 'Not Verified'}
                  </Chip>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Auth Provider</label>
                  <p className="text-base font-medium text-gray-900">
                    {currentUser.providerData?.[0]?.providerId === 'google.com'
                      ? '🔵 Google'
                      : currentUser.providerData?.[0]?.providerId === 'password'
                        ? '🔑 Email/Password'
                        : currentUser.providerData?.[0]?.providerId || 'Unknown'}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};
